import { ColliClient } from '@frontend/colli/api';
import { Colli, ColliStateName } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { IdentificationClient } from '@frontend/identification/api';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useScanners } from '../../../hooks';
import { isColliAtOtherLocation } from '../../../utils/colli-location';
import { useScreen } from '../useScreen';

interface ViewProps {
    removeScan: (id: string) => void;
    resolveScan: (value: string) => Promise<void>;
    expectedCollis: Colli[] | null;
    scannedCollis: Colli[];
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (screen: ScreenType) => void;
    resolving: boolean;
    error: string | null;
    resetError: () => void;
    isColliAtOtherLocation: (colli: Colli) => boolean;
}

const completedStates = [ColliStateName.COLLI_COMPLETED, ColliStateName.COLLI_COMPLETED_WITH_FAILURES];
const sortCollis = (collis: Colli[]): Colli[] =>
    [...collis].sort((a, b) => Number(completedStates.includes(a.state)) - Number(completedStates.includes(b.state)));

const usePickUpScanParcels = (): ViewProps => {
    const intl = useIntl();
    const { store, onBack, onSubmit: onContinue, updateWizardData, navigationState } = useScreen();
    const deviceSpotId = navigationState.iot?.spot_id ?? navigationState.spot?.id ?? undefined;
    const [resolving, setResolving] = useState<boolean>(false);
    const [expectedCollis, setExpectedCollis] = useState<Colli[] | null>(store?.data.collis ? sortCollis(store.data.collis) : null);
    const [scannedCollis, setScannedCollis] = useState<Colli[]>([]);
    const [mqttScanCallback, setMqttScanCallback] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useScanners({
        id: 'PICK_UP_SCAN_PARCELS',
        onDeviceSensorReceived: (code) => {
            setMqttScanCallback(code);
        }
    });

    useEffect(() => {
        if (mqttScanCallback) {
            resolveScan(mqttScanCallback).finally(() => {
                setMqttScanCallback(null);
            });
        }
    }, [mqttScanCallback]);

    useEffect(() => {
        if (!expectedCollis && store?.data.transaction)
            ColliClient.fetchCollis({ transaction_id: store.data.transaction.id }).then((res) => setExpectedCollis(sortCollis(res.results)));
    }, [expectedCollis, store?.data.transaction]);

    const resolveScan = async (value: string) => {
        if (resolving) return;
        setResolving(true);
        const colliResult = await ColliClient.resolveCollis([value]).catch((_) => {
            return { results: [], count: 0 };
        });
        if (colliResult.results.length > 0) {
            const found = colliResult.results[0];
            if (expectedCollis?.find((c) => c.id === found.id)) {
                setScannedCollis((prev) => (prev.some((c) => c.id === found.id) ? prev : [...prev, found]));
                setResolving(false);
                return;
            } else {
                setError(
                    intl.formatMessage({
                        id: 'mobile.wizard.dropoff.parcel-belongs-to-other-transaction',
                        defaultMessage: 'This parcel belongs to another transaction.',
                        description: 'Warning message when trying to add a parcel that belongs to another transaction than the other added parcels'
                    })
                );
                setResolving(false);
                return;
            }
        }
        const identificationsResult = await IdentificationClient.fetchIdentifications({ key: value, entity_type: EntityType.COLLI }).catch((_) => {
            return { results: [], count: 0 };
        });
        if (identificationsResult.results.length > 0) {
            const idColliResult = await ColliClient.resolveCollis(identificationsResult.results.map((i) => i.entity_id).filter((id): id is string => !!id));
            if (idColliResult.results.length > 0) {
                const found = idColliResult.results[0];
                if (expectedCollis?.find((c) => c.id === found.id)) {
                    setScannedCollis((prev) => (prev.some((c) => c.id === found.id) ? prev : [...prev, found]));
                    setResolving(false);
                    return;
                } else {
                    setError(
                        intl.formatMessage({
                            id: 'mobile.wizard.dropoff.parcel-belongs-to-other-transaction',
                            defaultMessage: 'This parcel belongs to another transaction.',
                            description: 'Warning message when trying to add a parcel that belongs to another transaction than the other added parcels'
                        })
                    );
                    setResolving(false);
                    return;
                }
            }
        }

        setError(
            intl.formatMessage({
                id: 'errors.unrecognizedCodePleaseTryAgain',
                description: 'Unrecognized code error message',
                defaultMessage: 'Unrecognized code, please try again.'
            })
        );
        setResolving(false);
    };

    const removeScan = (id: string) => {
        setScannedCollis((prev) => prev.filter((c) => c.id !== id));
    };

    const onSubmit = async () => {
        updateWizardData({ collis: scannedCollis });
        const nextStep = store?.steps.find(
            (step) =>
                step.screen === ScreenType.PICK_UP_SCAN_PARCELS &&
                [ScreenTrigger.SUBMIT_FALSE, ScreenTrigger.SUBMIT, ScreenTrigger.SUBMIT_TRUE].includes(step.trigger)
        );
        onContinue(nextStep);
    };

    const isSubmitDisabled = (): boolean => {
        return !expectedCollis || scannedCollis.length === 0;
    };

    return {
        removeScan,
        resolveScan,
        expectedCollis,
        scannedCollis,
        submitDisabled: isSubmitDisabled(),
        onSubmit,
        onBack,
        resolving,
        error,
        resetError: () => setError(null),
        isColliAtOtherLocation: (colli) => isColliAtOtherLocation(colli, deviceSpotId)
    };
};

export default usePickUpScanParcels;
